import { shopOperationError } from "./shopLabels";
import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Link } from "react-router-dom";
import { updateAccountShop, getShopDeletionSummary, deleteAccountShop, transferShopProducts } from "../auth/apiShops";
import { loadAccountShops, ACTIVE_SHOP_EVENT } from "./accountShopsStore";
import { elakayAutoText as text } from "../pageInside/dataElakay";
import ShopAddressEditor from "./ShopAddressEditor";

// Modal state lives outside the conditionally mounted My Shops dropdown.
// Standalone use remains supported when modalOnly/actionsOnly are omitted.
export default function ShopManagementActions({
  shop,
  shops = [],
  actionsOnly = false,
  modalOnly = false,
  initialMode = null,
  onRequestOpen,
  onClose,
}) {
  const [mode, setMode] = useState(initialMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState(shop.name);
  const [summary, setSummary] = useState(null);
  const [destination, setDestination] = useState("");
  const targets = shops.filter((s) => s.type === shop.type && String(s._id) !== String(shop._id));

  const refresh = async () => {
    await loadAccountShops({ force: true });
    window.dispatchEvent(new CustomEvent(ACTIVE_SHOP_EVENT, { detail: { type: shop.type } }));
  };

  const close = () => {
    if (busy) return;
    setMode(null);
    onClose?.();
  };

  const perform = async (operation) => {
    setBusy(true);
    setError("");
    try {
      await operation();
      await refresh();
      setMode(null);
      onClose?.();
    } catch (failure) {
      setError(shopOperationError(failure));
    } finally {
      setBusy(false);
    }
  };

  const open = (next) => {
    if (onRequestOpen) { onRequestOpen(next); return; }
    setMode(next);
  };

  useEffect(() => {
    if (!mode) return undefined;
    let cancelled = false;
    setError("");
    setDestination("");
    setSummary(null);
    setName(shop.name);
    if (mode !== "delete" && mode !== "transfer") return undefined;
    setBusy(true);
    getShopDeletionSummary(shop._id)
      .then((result) => { if (!cancelled) setSummary(result); })
      .catch((failure) => { if (!cancelled) setError(shopOperationError(failure)); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [mode, shop._id, shop.name]);

  return (
    <>
      {!modalOnly && (
        <div className="msh-pending-actions">
          {shop.publicPath && <Link className="msh-action" to={shop.publicPath}>{text("h_msh_view_shop", "View this shop as a visitor")}</Link>}
          <button type="button" className="msh-action" onClick={() => open("edit")}>{text("h_msl_edit", "Edit Shop")}</button>
          <button type="button" className="msh-action" onClick={() => open("transfer")}>{text("h_msl_transfer", "Transfer Products")}</button>
          <button type="button" className="msh-action msh-action-danger" onClick={() => open("delete")}>{text("h_msl_delete", "Delete Shop")}</button>
          {!mode && error && <div role="alert">{error}</div>}
        </div>
      )}
      {!actionsOnly && (
        <Modal
          show={Boolean(mode)}
          onHide={close}
          backdrop="static"
          keyboard={!busy}
          centered
          className="msh-management-modal"
          backdropClassName="msh-management-backdrop"
          container={() => document.body}
          restoreFocus
        >
          <Modal.Header closeButton={!busy}>
            <Modal.Title>
              {shop.name} · {text(mode === "edit" ? "h_msl_edit" : mode === "transfer" ? "h_msl_transfer" : "h_msl_delete", mode === "edit" ? "Edit Shop" : mode === "transfer" ? "Transfer Products" : "Delete Shop")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {mode === "edit" ? (
              <>
                <label className="d-block mb-2">
                  {text("h_msl_name", "Name")}
                  <input className="form-control" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
                </label>
                <ShopAddressEditor shop={shop} />
                <button type="button" className="btn btn-primary mt-2" disabled={busy || name.trim().length < 2} onClick={() => perform(() => updateAccountShop(shop._id, { name }))}>{text("h_msl_save", "Save")}</button>
              </>
            ) : (
              <>
                {busy && <div role="status">{text("h_msl_loading", "Loading…")}</div>}
                {summary && <p>{text("h_msl_counts", "{total} products: {exclusive} exclusive, {shared} shared.", summary)}</p>}
                {mode === "delete" && <p>{text("h_msl_delete_now", "The shop is deleted now. No product is deleted: shared products stay in their other shops, products listed only in this shop are kept but deactivated (reassign them from Edit Product), and past orders are preserved.")}</p>}
                <label className="d-block">
                  {text("h_msl_destination", "Select Destination Shop")}
                  <select className="form-control" value={destination} onChange={(event) => setDestination(event.target.value)}>
                    <option value="">{text("h_msl_no_transfer", "Without transfer")}</option>
                    {targets.map((target) => <option key={target._id} value={target._id}>{target.name}</option>)}
                  </select>
                </label>
                {mode === "transfer" && <button type="button" className="btn btn-primary mt-3" disabled={busy || !destination || !summary} onClick={() => perform(() => transferShopProducts(shop._id, destination))}>{text("h_msl_transfer", "Transfer Products")}</button>}
                {mode === "delete" && <button type="button" className="btn btn-danger mt-3" disabled={busy || !summary} onClick={() => window.confirm(text("h_msl_delete_confirm", "Delete {name} now?", { name: shop.name })) && perform(() => deleteAccountShop(shop._id, destination || undefined))}>{text("h_msl_delete", "Delete Shop")}</button>}
              </>
            )}
            {error && <div className="msh-alert msh-alert-error" role="alert">{error}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}
